// A Case's Related: one row per thing, gathered by the server from the links
// added by hand, the references in the Context and the steps, and the wait
// targets, each with where it came from. Only rows added by hand can be taken
// off. "Add" searches Cases and people, or takes a pasted URL.
import { useQuery } from "@tanstack/react-query";
import {
  CASE_LINK_URL_MAX,
  type CaseDetail,
  type CaseRelated,
  type CaseRelatedFrom,
  type CaseStep,
} from "@trameria/core/cases-shared";
import { m } from "@trameria/i18n";
import {
  type KeyboardEvent,
  type RefObject,
  useId,
  useRef,
  useState,
} from "react";
import { useApi, useMeApp } from "../app/context";
import { isOptimisticId } from "../data/optimistic";
import { useWorkspaceSource } from "../inbox/workspace";
import { refQuery, type SearchAnswer, searchQuery } from "../refs/queries";
import { joinList } from "../time/format";
import { Icon } from "../ui/icon";
import { isImeComposing } from "../ui/ime";
import { Popover } from "../ui/popover";
import { TargetName } from "./bits";
import type { CasePatch } from "./queries";

const UUID = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";
const ME_PATHS: ReadonlyArray<[RegExp, string]> = [
  [new RegExp(`^/me/cases/(${UUID})/?$`, "i"), "case"],
  [new RegExp(`^/me/people/(${UUID})/?$`, "i"), "person"],
];

/** A pasted http(s) URL (as written), or null. */
export function pastedUrl(text: string): string | null {
  const t = text.trim();
  if (t === "" || t.length > CASE_LINK_URL_MAX || /\s/.test(t)) return null;
  try {
    const u = new URL(t);
    return u.protocol === "http:" || u.protocol === "https:" ? t : null;
  } catch {
    return null;
  }
}

/** A URL of this app's Me (`/me/cases/<id>`, `/me/people/<id>`) as a reference. */
export function meRefOfUrl(
  url: string,
  origin: string,
): { kind: string; id: string } | null {
  const u = new URL(url);
  if (u.origin !== origin) return null;
  for (const [re, kind] of ME_PATHS) {
    const hit = re.exec(u.pathname);
    if (hit) return { kind, id: hit[1].toLowerCase() };
  }
  return null;
}

function slotWord(
  slot: "body" | "result" | "wait",
  step: CaseStep | undefined,
) {
  const kind =
    step?.kind === "waiting" ? m.cases_kind_waiting() : m.cases_kind_action();
  const title = step?.title ?? "";
  if (slot === "body") return m.cases_source_step_body({ kind, title });
  if (slot === "result") return m.cases_source_step_result({ kind, title });
  return m.cases_source_step_wait({ kind, title });
}

/** Where a row came from, written out (hand-added rows say nothing). */
export function sourceText(
  sources: readonly CaseRelatedFrom[],
  steps: readonly CaseStep[],
): string | null {
  const words = new Set<string>();
  for (const s of sources) {
    if (s.from === "manual") continue;
    if (s.from === "context") words.add(m.cases_context());
    else
      words.add(
        slotWord(
          s.slot,
          steps.find((x) => x.id === s.stepId),
        ),
      );
  }
  return words.size > 0 ? joinList([...words]) : null;
}

export function Related({
  c,
  write,
}: {
  c: CaseDetail;
  write(patch: CasePatch, lead: string): void;
}) {
  const [open, setOpen] = useState(false);
  const add = useRef<HTMLButtonElement>(null);
  return (
    <div data-related="">
      {c.related.length > 0 && (
        <ul className="flex flex-col">
          {c.related.map((r) => (
            <RelatedRow
              key={`${r.kind}:${r.targetId}`}
              c={c}
              r={r}
              write={write}
            />
          ))}
        </ul>
      )}
      <button
        ref={add}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={m.cases_related_add()}
        onClick={() => setOpen((v) => !v)}
        className="-ml-[6px] inline-flex cursor-pointer items-center gap-[8px] rounded-[6px] px-[6px] py-[4px] text-[13px] text-muted hover:bg-hover hover:text-body"
      >
        <Icon kind="plus" />
        {m.cases_add()}
      </button>
      {open && (
        <LinkPicker
          anchor={add}
          c={c}
          onClose={() => setOpen(false)}
          onPick={(link) => {
            setOpen(false);
            write(
              { add_links: [link] },
              m.cases_related_add_failed({
                name: link.label ?? link.target_id,
              }),
            );
          }}
        />
      )}
    </div>
  );
}

function RelatedRow({
  c,
  r,
  write,
}: {
  c: CaseDetail;
  r: CaseRelated;
  write(patch: CasePatch, lead: string): void;
}) {
  const { favoriteKinds } = useMeApp();
  const manual = r.sources.find(
    (s): s is Extract<CaseRelatedFrom, { from: "manual" }> =>
      s.from === "manual",
  );
  const source = sourceText(r.sources, c.steps);
  const KindIcon = favoriteKinds[r.kind]?.icon;
  const name = r.target.label || r.targetId;
  return (
    <li
      data-related-row={`${r.kind}:${r.targetId}`}
      className="group relative -mx-[6px] flex min-w-0 items-center gap-[8px] rounded-[6px] py-[4px] pr-[30px] pl-[6px] text-[13px] text-body hover:bg-hover pointer-coarse:min-h-[44px] pointer-coarse:pr-[48px]"
    >
      {r.kind === "url" ? (
        <a
          href={r.targetId}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex min-w-0 shrink items-center gap-[8px] hover:underline"
        >
          <Icon kind="link" className="text-muted" />
          <span className="min-w-0 truncate">{name}</span>
        </a>
      ) : (
        <span className="inline-flex min-w-0 shrink items-center gap-[8px]">
          {KindIcon ? (
            <KindIcon className="h-4 w-4 flex-none text-muted" />
          ) : (
            <Icon kind="link" className="text-muted" />
          )}
          <TargetName target={r.target} showIcon={false} />
        </span>
      )}
      {source && (
        <span className="min-w-0 flex-1 basis-0 truncate text-[11.5px] text-faint">
          {source}
        </span>
      )}
      {manual && !isOptimisticId(manual.linkId) && (
        <button
          type="button"
          aria-label={m.cases_related_remove({ name })}
          onClick={(e) => {
            focusAfterRemoval(e.currentTarget);
            write(
              { remove_links: [manual.linkId] },
              m.cases_related_remove_failed({ name }),
            );
          }}
          className="absolute top-1/2 right-[4px] inline-flex h-5 w-5 pointer-coarse:h-[44px] pointer-coarse:w-[44px] -translate-y-1/2 cursor-pointer items-center justify-center rounded-[5px] text-muted opacity-0 hover:bg-selected hover:text-body focus:opacity-100 group-hover:opacity-100 pointer-coarse:opacity-100"
        >
          <Icon kind="close" size={11} />
        </button>
      )}
    </li>
  );
}

/**
 * Removing takes the row away with the focus in it: move the focus first to
 * the next row, else the previous one, else the "Add" button below the list,
 * so it never falls to the body.
 */
function focusAfterRemoval(removeBtn: HTMLElement) {
  const li = removeBtn.closest("li");
  const firstFocusable = (el: Element | null) =>
    el?.querySelector<HTMLElement>("a[href], button") ?? null;
  const target =
    firstFocusable(li?.nextElementSibling ?? null) ??
    firstFocusable(li?.previousElementSibling ?? null) ??
    li
      ?.closest("[data-related]")
      ?.querySelector<HTMLElement>(":scope > button");
  target?.focus({ preventScroll: true });
}

interface Candidate {
  kind: string;
  target_id: string;
  label: string;
}

function LinkPicker({
  anchor,
  c,
  onClose,
  onPick,
}: {
  anchor: RefObject<HTMLElement | null>;
  c: CaseDetail;
  onClose(): void;
  onPick(link: Candidate): void;
}) {
  const api = useApi();
  const { favoriteKinds } = useMeApp();
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(0);
  const q = query.trim();
  const url = pastedUrl(q);
  const meRef = url ? meRefOfUrl(url, window.location.origin) : null;
  const resolved = useQuery({
    ...refQuery(api, meRef?.kind ?? "", meRef?.id ?? ""),
    enabled: meRef !== null,
  });
  const search = useQuery({
    ...searchQuery(api, url === null ? q : "", ["case", "person"]),
    placeholderData: (previous) => previous,
  });
  // A Hub's desktop: the workspaces' things too, from the cloud.
  const workspace = useWorkspaceSource();
  const far = useQuery({
    queryKey: ["me", "workspace", "search", q] as const,
    queryFn: () =>
      api<SearchAnswer>(
        `${workspace?.search}?q=${encodeURIComponent(q)}&limit=10`,
      ),
    enabled: workspace !== undefined && url === null && q !== "",
    placeholderData: (previous) => previous,
    retry: false,
  });
  const taken = new Set(c.related.map((r) => `${r.kind}:${r.targetId}`));
  taken.add(`case:${c.id}`);
  let items: Candidate[] = [];
  if (meRef) {
    const r = resolved.data;
    if (r?.visible)
      items = [{ kind: meRef.kind, target_id: r.id, label: r.label }];
  } else if (url) items = [{ kind: "url", target_id: url, label: url }];
  else if (q !== "")
    items = [
      ...["case", "person"].flatMap((type) =>
        (search.data?.results[type] ?? []).map((h) => ({
          kind: h.kind,
          target_id: h.id,
          label: h.label,
        })),
      ),
      ...Object.values(far.data?.results ?? {}).flatMap((hits) =>
        hits.map((h) => ({ kind: h.kind, target_id: h.id, label: h.label })),
      ),
    ];
  items = items.filter((i) => !taken.has(`${i.kind}:${i.target_id}`));
  const listId = useId();
  const active = Math.min(selected, items.length - 1);
  const optionId = (i: number) => `${listId}-${i}`;
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (isImeComposing(e.nativeEvent)) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (items.length === 0) return;
      const step = e.key === "ArrowDown" ? 1 : -1;
      setSelected((Math.max(active, 0) + step + items.length) % items.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = items[active];
      if (item) onPick(item);
    }
  };
  return (
    <Popover
      anchor={anchor}
      open
      onClose={onClose}
      label={m.cases_related_add()}
      className="w-[340px] max-w-[calc(100vw-16px)]"
    >
      <div className="px-2 pt-1 pb-1.5">
        <input
          type="text"
          role="combobox"
          aria-label={m.cases_related_search()}
          aria-expanded="true"
          aria-controls={listId}
          aria-activedescendant={active >= 0 ? optionId(active) : undefined}
          aria-autocomplete="list"
          placeholder={m.cases_related_search_placeholder()}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setSelected(0);
          }}
          onKeyDown={onKeyDown}
          // biome-ignore lint/a11y/noAutofocus: opened to search
          autoFocus
          className="plain-field w-full rounded-[6px] border border-field bg-surface px-2 py-[5px] text-[12.5px] text-body outline-none focus:border-field-strong"
        />
      </div>
      <div id={listId} role="listbox" aria-label={m.cases_related_candidates()}>
        {q !== "" && items.length === 0 && !search.isFetching && (
          <p className="px-3 py-2 text-[12px] text-faint">
            {m.cases_related_none()}
          </p>
        )}
        {items.map((item, i) => {
          const KindIcon = favoriteKinds[item.kind]?.icon;
          return (
            <button
              key={`${item.kind}:${item.target_id}`}
              id={optionId(i)}
              type="button"
              role="option"
              tabIndex={-1}
              aria-selected={i === active}
              onMouseDown={(e) => {
                e.preventDefault();
                onPick(item);
              }}
              onMouseEnter={() => setSelected(i)}
              className={`flex h-8 w-full cursor-pointer items-center gap-2 px-3 text-left text-[13px] text-body ${
                i === active ? "bg-hover" : ""
              }`}
            >
              {KindIcon ? (
                <KindIcon className="h-4 w-4 flex-none text-muted" />
              ) : (
                <Icon kind="link" className="text-muted" />
              )}
              <span className="min-w-0 flex-1 truncate">{item.label}</span>
            </button>
          );
        })}
      </div>
    </Popover>
  );
}
